/** @format */

import { HomrScheduleContent } from '@shared/data-access/interfaces';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { EventBusService, Events } from '@shared/data-access/services';

import { CommonModule } from '@angular/common';
import { TimeService } from '@shared/utils/services';
import { Timer } from './timer';

const DEFAULT_TIMER_TEXT_COLOR = '#8d0d02';

@Component({
  selector: 'common-timer',
  templateUrl: './common-timer.component.html',
  styleUrl: './common-timer.component.scss',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonTimerComponent implements OnInit, OnDestroy {
  private readonly eventBusService = inject(EventBusService);
  private readonly timeService = inject(TimeService);

  /**
   * @description
   * homr schedule data
   */
  public readonly homrTimerContent = input<HomrScheduleContent>();

  /**
   * @description
   * Emits when timer running state changes
   */
  public readonly timerRunning = output<boolean>();

  /**
   * @description
   * is timer displayed
   */
  public readonly displayTimer = signal(false);

  /**
   * @description
   * Timer object
   */
  public readonly timer = signal<Timer | undefined>(undefined);

  /**
   * @description
   * Timer text color
   */
  public readonly textColor = computed(() => {
    const content = this.homrTimerContent();

    if (content?.foregroundColor && content.foregroundColor.trim() !== '') {
      return content.foregroundColor;
    }

    return DEFAULT_TIMER_TEXT_COLOR;
  });

  constructor() {
    // React to timer content changes
    effect(() => {
      const content = this.homrTimerContent();

      // Untrack the timer, we only need to watch for content changes
      const currentTimer = untracked(this.timer);
      if (currentTimer) {
        currentTimer.stopTimer();
      }

      if (content) {
        this.setTimer(content);

        const newTimer = untracked(this.timer);
        if (newTimer) {
          this.startTimer();
          this.displayTimer.set(true);
          this.timerRunning.emit(true);
        }
      }
    });
  }

  ngOnInit(): void {
    // catch the event
    this.eventBusService.on(Events.TimerEnded, () => {
      this.displayTimer.set(false);
      this.timerRunning.emit(false);
    });
  }

  ngOnDestroy() {
    const currentTimer = this.timer();
    if (currentTimer) {
      currentTimer.stopTimer();
    }
    this.eventBusService.unsubscribe(Events.TimerEnded);
  }

  setTimer(homrScheduleContent?: HomrScheduleContent) {
    if (homrScheduleContent !== undefined) {
      this.timer.set(
        new Timer(
          homrScheduleContent.startDate,
          homrScheduleContent.endDate,
          this.eventBusService,
          this.timeService,
        ),
      );
    }
  }

  startTimer() {
    this.timer()?.startTimer();
  }
}
